import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English bundles eagerly so the default visitor sees their text on first
// paint with zero flash. The other 13 locales are split out as separate
// webpack chunks via dynamic import() — a non-English visitor downloads
// only their chosen locale (~10 KB gzipped) on first visit, after which
// it's HTTP-cached. This keeps ~120 KB of unused JSON out of main.js.
import en from './locales/en/translation.json';
// Canonical locale list (codes + RTL) lives in ./locales/registry.mjs.
// Adding a locale here is just adding it there + adding a matching loader
// to LOCALE_LOADERS below.
import { LOCALE_CODES, RTL_LOCALE_CODES } from './locales/registry.mjs';

const SUPPORTED = LOCALE_CODES;
const RTL_LANGS = RTL_LOCALE_CODES;

// One dynamic import per locale — each becomes its own webpack chunk.
// Static import paths are required so webpack can statically analyse them.
const LOCALE_LOADERS = {
  es: () => import(/* webpackChunkName: "locale-es" */ './locales/es/translation.json'),
  fr: () => import(/* webpackChunkName: "locale-fr" */ './locales/fr/translation.json'),
  pl: () => import(/* webpackChunkName: "locale-pl" */ './locales/pl/translation.json'),
  bg: () => import(/* webpackChunkName: "locale-bg" */ './locales/bg/translation.json'),
  ru: () => import(/* webpackChunkName: "locale-ru" */ './locales/ru/translation.json'),
  ro: () => import(/* webpackChunkName: "locale-ro" */ './locales/ro/translation.json'),
  it: () => import(/* webpackChunkName: "locale-it" */ './locales/it/translation.json'),
  pt: () => import(/* webpackChunkName: "locale-pt" */ './locales/pt/translation.json'),
  de: () => import(/* webpackChunkName: "locale-de" */ './locales/de/translation.json'),
  tr: () => import(/* webpackChunkName: "locale-tr" */ './locales/tr/translation.json'),
  ar: () => import(/* webpackChunkName: "locale-ar" */ './locales/ar/translation.json'),
  hi: () => import(/* webpackChunkName: "locale-hi" */ './locales/hi/translation.json'),
  zh: () => import(/* webpackChunkName: "locale-zh" */ './locales/zh/translation.json'),
};

async function loadLocale(lng) {
  // Normalise 'en-GB' → 'en', 'zh-CN' → 'zh' etc.
  const code = String(lng || 'en').split('-')[0].toLowerCase();
  if (code === 'en') return; // already bundled
  if (i18n.hasResourceBundle(code, 'translation')) return; // already loaded
  const loader = LOCALE_LOADERS[code];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(code, 'translation', mod.default || mod, true, true);
    // After a fresh load i18next's t() output for the active language can
    // be stale until React re-renders. Emit languageChanged again so any
    // mounted components re-translate using the newly available bundle.
    if (i18n.language && i18n.language.split('-')[0].toLowerCase() === code) {
      i18n.changeLanguage(i18n.language);
    }
  } catch (e) {
    // Network failure → English fallback handles the user gracefully.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`i18n: failed to load locale ${code}`, e);
    }
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    supportedLngs: SUPPORTED,
    fallbackLng: 'en',
    nonExplicitSupportedLngs: true, // 'en-GB' → 'en', 'zh-CN' → 'zh', etc.
    interpolation: { escapeValue: false },
    detection: {
      // Priority order: the URL ?lng=xx wins (deep links / share), then the
      // user's saved choice, then the browser language, then the html tag.
      // Persist the resolved choice to localStorage so it survives refreshes
      // and cross-page navigation — the pre-fix behaviour reset to 'en' on
      // every load, which made the 13-language switcher pointless.
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      lookupQuerystring: 'lng',
      lookupLocalStorage: 'aen-lang',
      caches: ['localStorage'],
    },
  });

// Kick off loading the detected language immediately. For an English visitor
// this is a no-op; for everyone else the locale chunk downloads in parallel
// with the rest of the page so the brief English flash is minimised.
loadLocale(i18n.language);

i18n.on('languageChanged', (lng) => {
  // Keep <html lang>/<dir> in sync.
  document.documentElement.lang = lng;
  document.documentElement.dir = RTL_LANGS.includes(lng) ? 'rtl' : 'ltr';
  // Trigger lazy load if needed (no-op if already loaded).
  loadLocale(lng);
});

export default i18n;
